// Trusted hosts for the post-reset `redirect_url`. Same-origin is always allowed; any
// other host must be an exact match of, or a subdomain of, these Crehana domains.
const TRUSTED_HOST_SUFFIXES = ['crehana.com', 'creha.co'] as const;

/**
 * Open-redirect guard for the success CTA. The BE-provided `redirect_url` is attacker-
 * influenceable in principle, so before navigating the browser to an ABSOLUTE url we
 * confirm it points either at our own origin or at a trusted Crehana domain. Anything
 * else (or an unparseable url) is rejected so the caller can fall back to a safe relative
 * path.
 *
 * @param value    The candidate absolute url (e.g. the mutation's `redirect_url`).
 * @param origin   The current window origin. Passed in (not read globally) so the util is
 *                 pure and unit-testable.
 */
export const isTrustedRedirectUrl = (
  value: string,
  origin: string,
): boolean => {
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return false;
  }

  // Only http(s) may drive a browser navigation (blocks javascript:, data:, etc.).
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false;
  }

  if (url.origin === origin) {
    return true;
  }

  const host = url.hostname.toLowerCase();
  return TRUSTED_HOST_SUFFIXES.some(
    suffix => host === suffix || host.endsWith(`.${suffix}`),
  );
};
